import type { FC } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
	Form,
	FormField,
	FormItem,
	FormLabel,
	FormDescription,
	FormControl,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/ui/use-toast";
import { NotificationToggle } from "./NotificationToggle";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";

const emailPreferencesSchema = z.object({
	emailDigest: z.enum(["none", "daily", "weekly", "monthly"]),
	marketingEmails: z.boolean(),
	securityEmails: z.boolean(), // これは常にtrueである必要があります
});

type EmailPreferencesValues = z.infer<typeof emailPreferencesSchema>;

export const EmailPreferences: FC = () => {
	const { toast } = useToast();

	const form = useForm<EmailPreferencesValues>({
		resolver: zodResolver(emailPreferencesSchema),
		defaultValues: {
			emailDigest: "weekly",
			marketingEmails: true,
			securityEmails: true,
		},
	});

	const onSubmit = async (data: EmailPreferencesValues) => {
		try {
			// TODO: メール設定更新のAPI呼び出し
			toast({
				title: "メール設定を更新しました",
				description: "変更内容が保存されました",
			});
		} catch (error) {
			toast({
				title: "エラーが発生しました",
				description: "設定の更新に失敗しました",
				variant: "destructive",
			});
		}
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
				<div className="space-y-4">
					<FormField
						control={form.control}
						name="emailDigest"
						render={({ field }) => (
							<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
								<div className="space-y-0.5">
									<FormLabel className="text-base">
										アクティビティダイジェスト
									</FormLabel>
									<FormDescription>
										定期的なアクティビティの要約メール
									</FormDescription>
								</div>
								<Select
									onValueChange={field.onChange}
									defaultValue={field.value}
								>
									<FormControl>
										<SelectTrigger className="w-[160px]">
											<SelectValue placeholder="頻度を選択" />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										<SelectItem value="none">受信しない</SelectItem>
										<SelectItem value="daily">毎日</SelectItem>
										<SelectItem value="weekly">毎週</SelectItem>
										<SelectItem value="monthly">毎月</SelectItem>
									</SelectContent>
								</Select>
							</FormItem>
						)}
					/>

					<NotificationToggle
						name="marketingEmails"
						title="マーケティングメール"
						description="新機能や更新情報に関するメール"
						control={form.control}
					/>

					<NotificationToggle
						name="securityEmails"
						title="セキュリティ通知"
						description="アカウントのセキュリティに関する重要な通知"
						control={form.control}
					/>
				</div>
				<Button type="submit">変更を保存</Button>
			</form>
		</Form>
	);
};
